// Info drawer component — displays player-friendly event summaries

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';
import { debugState } from '../../state/debug-state.js';

/**
 * @typedef {object} InfoMessage
 * @property {number} id - Sequential message ID
 * @property {string} summary - Player-friendly summary text
 * @property {string} eventType - The event type that generated this message
 * @property {number} timestamp - When the message was received
 * @property {boolean} highlighted - Whether this message should flash
 */

class InfoDrawer extends BaseComponent {
  static properties = {
    messages: { type: Array },
    _showJumpToEnd: { type: Boolean, state: true },
    _debug: { type: Boolean, state: true },
  };

  static styles = css`
    :host {
      display: block;
      height: 100%;
    }

    .info-drawer {
      display: flex;
      flex-direction: column;
      height: 100%;
      position: relative;
    }

    .info-header {
      color: white;
      font-size: 18px;
      font-weight: 600;
      margin: 0 0 8px 0;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .message-count {
      font-size: 12px;
      color: #999;
      font-weight: 400;
    }

    .messages-list {
      display: flex;
      flex-direction: column;
      gap: 6px;
      overflow-y: auto;
      flex: 1;
      scroll-behavior: smooth;
    }

    .empty-state {
      color: #999;
      font-size: 14px;
      padding: 20px;
      text-align: center;
    }

    .message-item {
      background: #333;
      border: 1px solid #555;
      border-radius: 8px;
      padding: 10px 12px;
      transition: all 0.2s;
    }

    .message-item:hover {
      background: #444;
      border-color: #666;
    }

    .message-item.highlighted {
      animation: flash 1.5s ease-out;
    }

    @keyframes flash {
      0% {
        background: #1a3a5c;
        border-color: #3b82f6;
      }
      100% {
        background: #333;
        border-color: #555;
      }
    }

    .message-summary {
      color: white;
      font-size: 13px;
      line-height: 1.4;
    }

    .message-meta {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 2px;
    }

    .message-timestamp {
      color: #777;
      font-size: 11px;
    }

    .message-debug {
      color: #555;
      font-size: 10px;
      font-family: monospace;
    }

    .jump-to-end {
      position: absolute;
      bottom: 8px;
      left: 50%;
      transform: translateX(-50%);
      background: #3b82f6;
      color: white;
      border: none;
      border-radius: 16px;
      padding: 6px 14px;
      font-size: 12px;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 4px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
      transition: background 0.2s;
    }

    .jump-to-end:hover {
      background: #2563eb;
    }

    .jump-arrow {
      font-size: 14px;
    }
  `;

  constructor() {
    super();
    /** @type {InfoMessage[]} */
    this.messages = [];
    this._userScrolledUp = false;
    this._showJumpToEnd = false;
    this._debug = debugState.enabled;
    /** @type {(() => void) | null} */
    this._unsubscribeDebug = null;
  }

  async connectedCallback() {
    await super.connectedCallback?.();
    this._debug = debugState.enabled;
    this._unsubscribeDebug = debugState.subscribe(() => {
      this._debug = debugState.enabled;
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this._unsubscribeDebug?.();
    this._unsubscribeDebug = null;
  }

  /** @param {Map<string, unknown>} changedProps */
  updated(changedProps) {
    if (changedProps.has('messages')) {
      const oldMessages = /** @type {InfoMessage[]|undefined} */ (changedProps.get('messages'));
      const oldLength = oldMessages?.length || 0;
      if (this.messages.length > oldLength) {
        if (this._userScrolledUp) {
          this._showJumpToEnd = true;
        } else {
          this._scrollToBottom();
        }
      }
    }
  }

  _scrollToBottom() {
    requestAnimationFrame(() => {
      const list = this.shadowRoot?.querySelector('.messages-list');
      if (list) {
        list.scrollTop = list.scrollHeight;
      }
      this._userScrolledUp = false;
      this._showJumpToEnd = false;
    });
  }

  _jumpToEnd() {
    this._scrollToBottom();
  }

  /** @param {Event} e */
  _handleScroll(e) {
    const list = /** @type {HTMLElement} */ (e.target);
    const atBottom = list.scrollHeight - list.scrollTop - list.clientHeight < 30;
    this._userScrolledUp = !atBottom;
    if (atBottom) {
      this._showJumpToEnd = false;
    }
  }

  /** @param {number} timestamp */
  _formatTimestamp(timestamp) {
    const now = Date.now();
    const diffMs = now - timestamp;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);

    if (diffSecs < 60) {
      return 'Just now';
    } else if (diffMins < 60) {
      return `${diffMins}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else {
      return new Date(timestamp).toLocaleString();
    }
  }

  render() {
    return html`
      <div class="info-drawer">
        <h2 class="info-header">
          Info
          <span class="message-count">${this.messages.length}</span>
        </h2>
        ${
          this.messages.length === 0
            ? html` <div class="empty-state">No events yet</div> `
            : html`
              <div class="messages-list" @scroll="${this._handleScroll}">
                ${this.messages.map(
                  (msg) => html`
                    <div class="message-item ${msg.highlighted ? 'highlighted' : ''}">
                      <div class="message-summary">${msg.summary}</div>
                      <div class="message-meta">
                        <span class="message-timestamp">${this._formatTimestamp(msg.timestamp)}</span>
                        ${this._debug ? html`<span class="message-debug">#${msg.id} ${msg.eventType}</span>` : ''}
                      </div>
                    </div>
                  `,
                )}
              </div>
            `
        }
        ${
          this._showJumpToEnd
            ? html`
              <button class="jump-to-end" @click="${this._jumpToEnd}">
                <span class="jump-arrow">&darr;</span> New messages
              </button>
            `
            : ''
        }
      </div>
    `;
  }
}

customElements.define('info-drawer', InfoDrawer);
